/**
 * ReadFocusFeature - Dims surrounding content and highlights the focused/hovered element
 * Helps users with cognitive disabilities focus on one section at a time
 */
import BaseFeature from './BaseFeature.js';

export default class ReadFocusFeature extends BaseFeature {
    constructor(widget, options = {}) {
        super(widget, options);
        this._overlay = null;
        this._mouseMoveHandler = null;
        this._raf = null;
        this._lastY = null;
        this._bandHeight = options.bandHeight || 100;
    }

    enable() {
        super.enable();
        this.addBodyClass('dhA11y-read-focus');
        this._createOverlay();
        this._bindMouse();
    }

    disable() {
        super.disable();
        this.removeBodyClass('dhA11y-read-focus');
        this._unbindMouse();
        this._removeOverlay();
        this._lastY = null;
    }

    /**
     * Create the dimming overlay with a transparent hole
     */
    _createOverlay() {
        this._overlay = document.createElement('div');
        this._overlay.className = 'dhA11y-read-focus-overlay';
        this._overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 999990;
            pointer-events: none;
            transition: opacity 0.2s ease;
        `;
        document.body.appendChild(this._overlay);
        this._applyRuler(window.innerHeight / 2);
    }

    /**
     * Remove overlay
     */
    _removeOverlay() {
        if (this._overlay) {
            this._overlay.remove();
            this._overlay = null;
        }
    }

    /**
     * Bind mouse move to track focused element
     */
    _bindMouse() {
        this._mouseMoveHandler = (e) => {
            // Ignore widget UI
            if (e.target.closest('#accessibility-widget') || e.target.closest('.accessibility-control-bar')) {
                this._clearFocus();
                return;
            }

            this._lastY = e.clientY;
            if (!this._raf) {
                this._raf = requestAnimationFrame(() => {
                    this._applyRuler(this._lastY);
                    this._raf = null;
                });
            }
        };

        document.addEventListener('mousemove', this._mouseMoveHandler);
    }

    /**
     * Unbind mouse
     */
    _unbindMouse() {
        if (this._mouseMoveHandler) {
            document.removeEventListener('mousemove', this._mouseMoveHandler);
            this._mouseMoveHandler = null;
        }
        if (this._raf) {
            cancelAnimationFrame(this._raf);
            this._raf = null;
        }
    }

    /**
     * Apply ruler overlay: dim above and below a horizontal band
     */
    _applyRuler(y) {
        if (!this._overlay) return;
        const h = Math.max(40, this._bandHeight);
        const top = Math.max(0, Math.round(y - (h / 2)));
        const bottom = Math.min(window.innerHeight, top + h);

        // Linear-gradient creates a clear band and dimmed areas outside
        this._overlay.style.background = `linear-gradient(
            to bottom,
            rgba(0,0,0,0.6) 0px,
            rgba(0,0,0,0.6) ${top}px,
            rgba(0,0,0,0) ${top}px,
            rgba(0,0,0,0) ${bottom}px,
            rgba(0,0,0,0.6) ${bottom}px,
            rgba(0,0,0,0.6) 100%
        )`;
    }

    /**
     * Clear focus highlight
     */
    _clearFocus() {
        if (this._overlay) {
            this._overlay.style.background = 'transparent';
        }
        this._lastY = null;
    }
}
